import React, { useState, useEffect, useRef } from "react";
import swal from "sweetalert";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import axios from "axios";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { API_URL } from "../../config";
import InvoiceBody from "./vat_quotation_body";
import { currentDateTime, dateTimeFormat } from "../../lib/functions";
import PrintIcon from "@material-ui/icons/Print";
import EmailIcon from '@material-ui/icons/Email';
import { AttachFile, Cancel } from '@material-ui/icons';
import moment from "moment";
import ReactToPrint from "react-to-print";
import "./css/invoice.css";
import commaNumber from "comma-number";
import {
  Modal,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from "@material-ui/core";

let format = commaNumber.bindWith(",", ".");

const printStyles = `
  .vat-print-root {
    box-sizing: border-box;
    width: 100%;
    margin: 0 auto;
    padding: 0;
  }
  .vat-print-root .invoice-title {
    text-align: center;
    margin: 8px 0 10px 0;
  }
  .vat-print-root .invoice-table {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  @media print {
    @page {
      size: A4 portrait;
      margin: 8mm;
    }

    body, html {
      width: auto !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
      font-size: 12px !important;
      background: #fff !important;
    }

    .no-print {
      display: none !important;
    }

    .vat-print-root {
      width: 100% !important;
      max-width: none !important;
      min-height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      page-break-after: auto !important;
      break-after: auto !important;
    }

    .invoice-table {
      width: 100% !important;
      margin-top: 0 !important;
    }

    .invoice-footer-bottom {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      position: static;
    }
  }
`;

const useEmailModalStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    minWidth: 500,
    maxWidth: 600,
  },
  selectedEmailsList: {
    maxHeight: 200,
    overflow: 'auto',
    marginTop: theme.spacing(2),
    border: '1px solid #eee',
    borderRadius: 4,
  },
  plusLinkDiv: {
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
}));

const EmailModal = ({ open, handleClose, ccEmail, setCcEmail, authInfo }) => {
  const classes = useEmailModalStyles();
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailOptions, setEmailOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCCEmails();
    }
  }, [open]);

  const fetchCCEmails = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/get-email-cc-names`,
        {},
        {
          headers: {
            'auth-token': authInfo.token,
            'Content-Type': 'application/json',
          },
        }
      );
      setEmailOptions(response.data.message);
    } catch (error) {
      console.error("Error fetching CC emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (selectedEmail && !ccEmail.some(email => email.email_cc_email === selectedEmail.email_cc_email)) {
      setCcEmail([...ccEmail, selectedEmail]);
      setSelectedEmail(null);
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setCcEmail(ccEmail.filter(email => email.email_cc_email !== emailToRemove.email_cc_email));
  };

  return (
    <Modal open={open} onClose={handleClose} className={classes.modal}>
      <div className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Add CC Emails
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} className={classes.plusLinkDiv}>
            <Autocomplete
              style={{ width: "100%" }}
              options={emailOptions}
              size="small"
              openOnFocus={true}
              getOptionLabel={(option) => option?.email_cc_email || ""}
              value={selectedEmail}
              onChange={(event, selectedObj) => {
                setSelectedEmail(selectedObj);
              }}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose a CC Email"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleAddEmail}
              disabled={!selectedEmail}
              className={classes.actionButton}
            >
              Add Email
            </Button>
          </Grid>
        </Grid>

        {ccEmail.length > 0 && (
          <div className={classes.selectedEmailsList}>
            <List dense>
              {ccEmail.map((email) => (
                <ListItem key={email.email_cc_email}>
                  <ListItemText
                    primary={email.email_cc_name || email.email_cc_email}
                    secondary={email.email_cc_email ? email.email_cc_email : ''}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveEmail(email)}
                      size="small"
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </div>
        )}

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            onClick={handleClose}
            color="primary"
            variant="contained"
          >
            Done
          </Button>
        </Box>
      </div>
    </Modal>
  );
};

const usePrintAbleSectionStyles = makeStyles((theme) => ({
  radioGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
  },
  radioLabel: {
    marginRight: theme.spacing(1),
    fontSize: '0.875rem',
  },
}));

const PrintAbleSection = React.forwardRef(({ authInfo, saleId }, ref) => {
  const classes = usePrintAbleSectionStyles();
  const [sale, setSale] = useState(null);
  const [email, setEmail] = useState('');
  const [institutionProfile, setInstitutionProfile] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [ccEmail, setCcEmail] = useState([]);
  const [vatOption, setVatOption] = useState('A');

  useEffect(() => {
    if (!saleId || Number.isNaN(Number(saleId))) {
      setSale(null);
      setEmail('');
      return;
    }
    getsale();
    getProfile();
  }, [saleId]);

  const handleVatOptionChange = (event) => {
    setVatOption(event.target.value);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const getProfile = async () => {
    await axios.get(
      `${API_URL}/api/get-institution`,
      { headers: { 'auth-token': authInfo.token } }
    ).then(res => {
      setInstitutionProfile(res.data);
    });
  };

  const getsale = async () => {
    if (!saleId || Number.isNaN(Number(saleId))) return;
    try {
      const saleRes = await axios.post(
        `${API_URL}/api/get-sales-with-details`,
        { reqPayload: { saleId: saleId, from: "invoice" } },
        { headers: { "auth-token": authInfo.token } }
      );

      const saleData = saleRes?.data?.message?.[0] || null;
      setSale(saleData);

      const customerId = saleData?.customer_id || saleData?.sale_customer_id;
      if (!customerId) return;

      try {
        const emailRes = await axios.post(
          `${API_URL}/api/get-email-address`,
          {
            reqPayload: {
              saleId: saleId,
              from: "invoice"
            },
            customerId
          },
          { headers: { "auth-token": authInfo.token } }
        );
        setEmail(emailRes?.data?.email || "");
      } catch (_emailError) {
        // Email is optional for VAT view; ignore address lookup failures.
        setEmail("");
      }
    } catch (_saleError) {
      setSale(null);
      setEmail("");
    }
  };

  const sendMail = async () => {
    if (!email || email.trim() === "") {
      swal({ title: "Customer email is empty", icon: "warning" });
      return false;
    }

    const ccEmails = ccEmail.map(emailObj => emailObj.email_cc_email);

    if (ccEmail.length < 1) {
      const confirmResult = await swal({
        title: "Do you want to send email without CC?",
        text: `To: ${email}${ccEmail.length > 0 ? `\nCC: ${ccEmails.join(', ')}` : ''}`,
        icon: "info",
        buttons: true,
      });
      if (confirmResult) {
        try {
          const res = await axios.post(
            `${API_URL}/api/send-quotation-invoice`,
            {
              reqPayload: {
                saleId: saleId,
                from: "invoice"
              },
              sale: sale,
              email: email,
              ccEmail: ccEmails,
              institutionProfile: institutionProfile
            },
            {
              headers: {
                "auth-token": authInfo.token,
                "Content-Type": "application/json"
              }
            }
          );

          if (!res.data.error) {
            swal({ title: "Email sent successfully!", icon: "success" });
          } else {
            throw new Error(res.data.message || "Failed to send email");
          }
        } catch (error) {
          console.error("Error sending mail:", error);
          swal({
            title: "Failed to send email",
            text: error.message,
            icon: "error"
          });
        }
      }
    } else if (ccEmail.length >= 1) {
      const confirmResult = await swal({
        title: "Do you want to send email with CC?",
        text: `To: ${email}${ccEmail.length > 0 ? `\nCC: ${ccEmails.join(', ')}` : ''}`,
        icon: "info",
        buttons: true,
      });
      if (confirmResult) {
        try {
          const res = await axios.post(
            `${API_URL}/api/send-quotation-invoice`,
            {
              reqPayload: {
                saleId: saleId,
                from: "invoice"
              },
              sale: sale,
              email: email,
              ccEmail: ccEmails,
              institutionProfile: institutionProfile
            },
            {
              headers: {
                "auth-token": authInfo.token,
                "Content-Type": "application/json"
              }
            }
          );

          if (!res.data.error) {
            swal({ title: "Email sent successfully!", icon: "success" });
          } else {
            throw new Error(res.data.message || "Failed to send email");
          }
        } catch (error) {
          console.error("Error sending mail:", error);
          swal({
            title: "Failed to send email",
            text: error.message,
            icon: "error"
          });
        }
      }
    }
  };

  const checkboxOptions = sale?.checkbox_options
    ? JSON.parse(sale.checkbox_options)
    : [];

  return (
    <div style={{
      paddingTop: "0mm",
      paddingBottom: "0mm",
      paddingLeft: "5mm",
      paddingRight: "5mm",
      position: "relative",
    }}
    >
      {/* Non-printable action buttons */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        {/* <span onClick={sendMail}><EmailIcon style={{ cursor: "pointer", marginRight: 10 }} /></span> */}
        {/* <span onClick={handleOpenModal}><AttachFile style={{ cursor: "pointer", marginRight: 10 }} /></span> */}
        <Grid container>
          <Grid item xs={12} sm={12}>
            <h3 className={"invoice-title"}>VAT Invoice</h3>
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormControl component="fieldset" className={classes.radioGroup}>
              <RadioGroup
                row
                aria-label="vat-option"
                name="vat-option"
                value={vatOption}
                onChange={handleVatOptionChange}
              >
                <FormControlLabel value="A" control={<Radio size="small" />} label="Option A" />
                <FormControlLabel value="B" control={<Radio size="small" />} label="Option B" />
                <FormControlLabel value="C" control={<Radio size="small" />} label="Option C" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

      </div>

      {/* Printable content */}
      <div ref={ref} className="vat-print-root">
        {sale != null ? (
          <>
            <style>{printStyles}</style>
            <Grid container style={{ marginBottom: "12px", fontSize: "13px" }}>
              <Grid item xs={6} sm={8}>
                {/* <strong>Customer ID : </strong>
                <span>{sale.customer_code}</span>
                <br /> */}
                <strong>Customer Name : </strong>
                <span>{sale.customer_name}</span>
                <br />
                {/* <strong>Institution Name : </strong>
                <span>{sale.customer_institution_name}</span>
                <br /> */}
                <strong>Customer Address : </strong>
                <span>{sale.customer_address}</span>
                <br />
                <strong>Customer BIN: </strong>
                <span>{sale.customer_bin_number}</span>
                <br />
                {/* <strong>Customer Mobile : </strong>
                <span>{sale.customer_mobile_no}</span>
                <br /> */}
                {/* <strong>PO No : </strong> <span>{sale.sale_po_id}</span>
                <br /> */}
              </Grid>
              <Grid item xs={6} sm={4} style={{ textAlign: "left" }}>
                <strong>Sale By : </strong>
                <span style={{ textTransform: "uppercase" }}>{institutionProfile?.pro_name}</span>
                <br />
                <strong>BIN:  </strong>
                <span style={{ textTransform: "uppercase" }}>{"002323032-0301"}</span>
                <br />
                <strong>Ref By : </strong>
                <span style={{ textTransform: "uppercase" }}>{sale.employee_name}</span>
                <br />
                <strong>Invoice No : </strong> <span>{sale.sale_invoice}</span>
                <br />
                <strong> Date & Time : </strong>
                <span>
                  {moment(sale.sale_created_isodt).format(dateTimeFormat)}
                </span>
                <br />
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12} sm={12}>
                <h3 className={"invoice-title"}>VAT Invoice</h3>
              </Grid>
            </Grid>

            <InvoiceBody saleData={sale} vatOption={vatOption} />

            {/* <Grid style={{marginTop: "80px"}}>
              <p><b><i>Authority's Name:</i></b> Muhammad Wahidul Islam</p>
              <p><b><i>Designation:</i></b>      Manager</p>
              <p><b><i>Signature:</i></b>      </p>

            </Grid> */}
          </>
        ) : (
          ""
        )}
      </div>

      <EmailModal
        open={modalOpen}
        handleClose={handleCloseModal}
        ccEmail={ccEmail}
        setCcEmail={setCcEmail}
        authInfo={authInfo}
      />
    </div>
  );
});

let SalesVat = ({ authInfo, saleId, match }) => {
  const printRef = useRef();
  const routeSaleId = parseFloat(match?.params?.saleId);
  const fallbackSaleId = parseFloat(saleId);
  const resolvedSaleId = Number.isFinite(routeSaleId)
    ? routeSaleId
    : (Number.isFinite(fallbackSaleId) ? fallbackSaleId : null);

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={8} className={"invoice-section"}>
          <div>
            <ReactToPrint
              trigger={() => <PrintIcon style={{ cursor: "pointer" }} />}
              content={() => printRef.current}
            />

            <PrintAbleSection
              authInfo={authInfo}
              saleId={resolvedSaleId}
              ref={printRef}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
};

const mapStateToPops = (state) => {
  return {
    authInfo: state.authInfoReducer,
  };
};

export default connect(mapStateToPops, {})(SalesVat);
